/**
 * `App` — top-level application shell (Req 3.1, 17.4).
 *
 * Composes the persistent chrome (`Navbar` and `Footer`) around the routed
 * outlet produced by `useRoutes(routes)`. The `Navbar` and `Footer` are
 * deliberately mounted **outside** the per-route `RouteErrorBoundary`
 * wrappers defined in `router/routes.tsx`, so a failure inside any single
 * route only tears down the routed subtree while the header and footer stay
 * visible and interactive (Req 17.4).
 *
 * Layout ordering, top-to-bottom:
 *   1. `<Navbar>` — global navigation (primary menu, logo, brand).
 *   2. `<main>` — the routed outlet returned by `useRoutes(routes)`; each
 *      route entry in `router/routes.tsx` is already wrapped in its own
 *      `RouteErrorBoundary` + `Suspense`.
 *   3. `<Footer>` — global footer (footer menu, contact info, copyright).
 *
 * Data flow:
 *   - `useMenu('primary')` and `useMenu('footer')` resolve the two nav-menu
 *     trees exposed by the `headless-bridge` plugin.
 *   - `useSettings()` resolves the unified `SiteSettings` document (site
 *     name, logo, favicon, footer HTML, contact info, social links).
 *
 * All three hooks defer their caching semantics to the shared `QueryClient`
 * installed by `providers/AppProviders.tsx`. Each hook returns the raw
 * TanStack Query result; `data` is `undefined` while loading and the
 * navbar/footer are responsible for handling that gracefully. We normalise
 * to `null` at the composition boundary so the children work against a
 * clean "value or null" contract instead of "value or undefined".
 *
 * Requirements: 3.1 (layout structure with Navbar / main / Footer),
 * 17.4 (per-route error boundaries preserve outer chrome).
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import { Footer } from './components/Footer';
import { GlobalFooter } from './components/Footer/Footer';
import { GlobalPresence } from './components/contact/GlobalPresence';
import { BackToTop } from './components/BackToTop';
import { CookieConsent } from './components/CookieConsent';
import { ScrollToTop } from './components/ScrollToTop';
import { GlobalHeader } from './components/Header';
import { Navbar } from './components/Navbar';
import { MaintenancePage } from './pages/MaintenancePage';
import { useDesignSystem } from './hooks/useDesignSystem';
import { useMenu } from './hooks/useMenu';
import { useSettings } from './hooks/useSettings';
import { useRedirects } from './hooks/useRedirects';
import { routes } from './router/routes';

/** Mirrors the server-side cycle-detection cap in `class-redirects.php`'s
 *  `would_create_loop()` — a safety bound, not an expected chain length
 *  (the backend already refuses to save a cyclic chain; this only
 *  guards against an unbounded walk if that invariant is ever violated,
 *  e.g. by a direct database edit). */
const MAX_REDIRECT_HOPS = 10;

/**
 * Normalise an incoming pathname before matching it against the redirect
 * table. Trims a trailing slash (except bare `/`) to match how
 * `class-redirects.php`'s `normalize_path()` already normalises a rule's
 * `source_path` before it's stored, and additionally lowercases the path
 * for a more forgiving, case-insensitive match (the backend does not
 * lowercase on save, so this is a client-side robustness addition, not a
 * mirror of that specific behaviour).
 *
 * Without the trailing-slash fix, a rule saved for `/old-page` never
 * matched a real visit to `/old-page/` (browsers, old bookmarks, and
 * crawlers routinely add or omit a trailing slash) — the lookup below
 * was a raw `Map.get()` against `location.pathname` verbatim, so those
 * visits silently fell through to the 404 page instead of redirecting.
 * Query strings and hashes are already excluded from `location.pathname`
 * by the router, matching how the backend strips them from a saved
 * source path too.
 */
function normalizeRedirectPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.length > 1 && lower.endsWith('/')) {
    return lower.slice(0, -1);
  }
  return lower;
}

/**
 * Resolve the current path against the active redirect list managed
 * from WP Admin → Tools → 301 Redirections, following a chain of
 * internal hops (path → path → path → …) up to {@link MAX_REDIRECT_HOPS},
 * and returning either an internal path (for `<Navigate>`) or an
 * absolute URL (for a hard `window.location` navigation) — or `null`
 * when no redirect applies.
 */
function resolveRedirectTarget(
  path: string,
  rules: { source: string; destination: string }[],
): string | null {
  if (rules.length === 0) return null;

  const bySource = new Map(
    rules.map((r) => [normalizeRedirectPath(r.source), r.destination] as const),
  );
  let current = normalizeRedirectPath(path);
  let finalDestination: string | null = null;

  for (let hop = 0; hop < MAX_REDIRECT_HOPS; hop++) {
    const next = bySource.get(current);
    if (next === undefined) break;
    finalDestination = next;
    if (/^https?:\/\//i.test(next)) break; // external URL always terminates the chain
    current = normalizeRedirectPath(next);
  }

  return finalDestination;
}

/**
 * Route-change gate that applies the active 301/302 redirect list on
 * every navigation, before the matched route's content renders.
 *
 * This exists because the site is a headless SPA: Apache serves
 * `index.html` directly for any non-`/wp-*` path, so WordPress's own
 * redirect hooks never run for a normal page URL (see
 * `class-redirects.php`'s docblock for the full explanation). This is
 * the client-side enforcement half of that design — it covers every
 * real visitor and every JS-executing crawler.
 */
function useRedirectGate(): string | null {
  const location = useLocation();
  const { data: rules } = useRedirects();

  if (!rules || rules.length === 0) return null;
  return resolveRedirectTarget(location.pathname, rules);
}

/**
 * `sessionStorage` key used to remember a successful maintenance-mode
 * bypass for the current browser tab (cleared when the tab closes).
 */
const MAINTENANCE_BYPASS_KEY = 'otpl_maintenance_bypass';

/**
 * Resolve whether the current visitor should see the real site while
 * maintenance mode is enabled.
 *
 * Checks (in order):
 *   1. `?bypass=<token>` in the current URL matches the configured
 *      `bypassToken` — persists the bypass to `sessionStorage` so it
 *      survives client-side navigation without the query param.
 *   2. A previously persisted bypass flag in `sessionStorage`.
 *
 * wp-admin is never gated by this at all — Apache proxies `/wp-*`
 * paths straight to WordPress before the SPA is even involved, so
 * editors can always log in to turn maintenance mode back off.
 */
function useMaintenanceBypass(bypassToken: string): boolean {
  const [bypassed, setBypassed] = useState(false);

  useEffect(() => {
    if (bypassToken === '') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const provided = params.get('bypass');
    if (provided !== null && provided === bypassToken) {
      window.sessionStorage.setItem(MAINTENANCE_BYPASS_KEY, '1');
      setBypassed(true);
      return;
    }
    setBypassed(window.sessionStorage.getItem(MAINTENANCE_BYPASS_KEY) === '1');
  }, [bypassToken]);

  return bypassed;
}

/**
 * Application shell. Rendered once by `main.tsx` as
 * `<AppProviders><App /></AppProviders>`.
 *
 * The homepage (`/`) uses its own Navbar and Footer (glassmorphism + rich
 * footer with newsletter), so the global chrome is hidden on that route.
 */
export function App(): JSX.Element {
  const routeElement = useRoutes(routes);
  const location = useLocation();
  const primaryMenu = useMenu('primary');
  const footerMenu = useMenu('footer');
  const settings = useSettings();
  const s = settings.data;
  const bypassed = useMaintenanceBypass(s?.maintenance.bypassToken ?? '');
  const redirectTarget = useRedirectGate();

  // Load and inject design system tokens (colors, typography, UI settings)
  useDesignSystem();

  // Homepage has its own chrome (navbar + footer) built into the page
  const isSetupPage = location.pathname === '/setupcms';

  if (isSetupPage) {
    return (
      <>
        <ScrollToTop />
        <main>{routeElement}</main>
      </>
    );
  }

  // 301/302 redirect management (Tools → 301 Redirections) — checked
  // before route content renders, on every navigation. Internal targets
  // use a client-side <Navigate> (no full page reload); an external
  // target (an absolute https:// URL) needs a hard navigation instead.
  if (redirectTarget !== null) {
    if (/^https?:\/\//i.test(redirectTarget)) {
      window.location.replace(redirectTarget);
      return <></>;
    }
    return <Navigate to={redirectTarget} replace />;
  }

  // Site-wide maintenance mode — gates every route except /setupcms above.
  // `settings.data` is undefined on the very first render (query still
  // loading); rendering the normal site during that brief window rather
  // than flashing the maintenance page avoids a jarring flicker for the
  // common case (maintenance mode off) and self-corrects the moment the
  // settings document resolves.
  if (s?.maintenance.enabled && !bypassed) {
    return (
      <>
        <ScrollToTop />
        <MaintenancePage message={s.maintenance.message} />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <GlobalHeader />
      <main>{routeElement}</main>
      {/* Unified bg + dot pattern for GlobalPresence + Footer — seamless */}
      <div
        className="relative bg-slate-50 dark:bg-slate-900"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.35) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      >
        <GlobalPresence />
        <GlobalFooter />
      </div>
      <BackToTop />
      <CookieConsent />
    </>
  );
}

export default App;
